import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as api from '../api';
import { Trash2, Upload, FileText, Eye, X, Pencil } from 'lucide-react';
import { toast } from 'sonner';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    const s = typeof d === 'string' ? d.slice(0, 10) : new Date(d).toISOString().slice(0, 10);
    const [y, m, day] = s.split('-');
    return `${day}/${m}/${y}`;
  } catch { return '—'; }
}

function DocModal({ title, fileName, form, setForm, types, onConfirm, onCancel, loading, confirmLabel }) {
  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center"
      style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}
      data-ignore-outside="true"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
        style={{ border: '1px solid var(--border)' }}
        onMouseDown={e => e.stopPropagation()}
        data-ignore-outside="true"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div>
            <div className="text-sm font-bold" style={{ color: 'var(--text)' }}>{title}</div>
            {fileName && (
              <div className="text-xs mt-0.5 truncate max-w-xs" style={{ color: 'var(--text-muted)' }}>
                {fileName}
              </div>
            )}
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', marginTop: 2 }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text)' }}>
              Type de document
            </label>
            <select
              value={form.type_id}
              onChange={e => {
                const t = types.find(t => t.type_id === e.target.value);
                setForm(f => ({ ...f, type_id: e.target.value, type_nom: t?.nom || '' }));
              }}
              className="w-full text-sm px-3 py-2 rounded-lg border focus:outline-none"
              style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}
            >
              <option value="">— Sélectionner —</option>
              {types.map(t => <option key={t.type_id} value={t.type_id}>{t.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text)' }}>
              Numéro du document
            </label>
            <input
              value={form.numero}
              onChange={e => setForm(f => ({ ...f, numero: e.target.value }))}
              placeholder="Ex : BC-2025-001"
              className="w-full text-sm px-3 py-2 rounded-lg border focus:outline-none"
              style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text)' }}>
              Date du document
            </label>
            <input
              type="date"
              value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              className="w-full text-sm px-3 py-2 rounded-lg border focus:outline-none"
              style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm border"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'transparent' }}
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: 'var(--primary-solid)' }}
          >
            {loading ? 'En cours…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DocumentsTab({ entityId, listFn, uploadFn, deleteFn, updateFn, downloadFn, onCountChange, wideColumns = false }) {
  const [docs, setDocs]           = useState([]);
  const [types, setTypes]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [dragging, setDragging]   = useState(false);

  // Modal upload
  const [uploadModal, setUploadModal]   = useState(false);
  const [pendingFile, setPendingFile]   = useState(null);
  const [uploadForm, setUploadForm]     = useState({ type_id: '', type_nom: '', numero: '', date: todayISO() });
  const [uploading, setUploading]       = useState(false);

  // Modal édition
  const [editDoc, setEditDoc]   = useState(null);
  const [editForm, setEditForm] = useState({ type_id: '', type_nom: '', numero: '', date: '' });
  const [saving, setSaving]     = useState(false);

  // Aperçu image uniquement
  const [previewImg, setPreviewImg]   = useState(null);
  const [previewName, setPreviewName] = useState('');

  // ── Aperçu — même pattern exact que Locations "Visualiser" ──────────────
  const handlePreview = async (doc) => {
    const isImage = /\.(jpe?g|png|gif|webp|heic|heif|svg|bmp)$/i.test(doc.nom_fichier || '');
    if (isImage) {
      setPreviewImg(doc.url);
      setPreviewName(doc.nom_fichier);
      return;
    }
    if (!downloadFn) {
      window.open(doc.url, '_blank');
      return;
    }
    try {
      const resp = await downloadFn(doc.doc_id);
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank');
      if (w) w.focus();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error('Erreur lors du chargement du document');
    }
  };

  const inputRef = useRef();

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const [docsRes, typesRes] = await Promise.all([
        listFn().catch(() => ({ data: [] })),
        api.getUploadTypes().catch(() => ({ data: [] })),
      ]);
      const list = docsRes.data || [];
      setDocs(list);
      setTypes(typesRes.data || []);
      onCountChange?.(list.length);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [entityId, listFn, onCountChange]);

  useEffect(() => { load(); }, [load]);

  // ── Drop & sélection ───────────────────────────────────────
  const openUploadModal = (file) => {
    setPendingFile(file);
    setUploadForm({ type_id: '', type_nom: '', numero: '', date: todayISO() });
    setUploadModal(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) openUploadModal(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) openUploadModal(file);
    e.target.value = '';
  };

  // ── Upload confirm ─────────────────────────────────────────
  const handleUploadConfirm = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      const type = types.find(t => t.type_id === uploadForm.type_id);
      if (type) {
        fd.append('upload_type_id', type.type_id);
        fd.append('upload_type_nom', type.nom);
      }
      if (uploadForm.numero)   fd.append('numero', uploadForm.numero);
      if (uploadForm.date)     fd.append('date_document', uploadForm.date);
      await uploadFn(fd);
      toast.success('Document ajouté');
      setUploadModal(false);
      setPendingFile(null);
      await load();
    } catch {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  // ── Suppression ────────────────────────────────────────────
  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer "${doc.nom_fichier}" ?`)) return;
    try {
      await deleteFn(doc.doc_id);
      toast.success('Document supprimé');
      await load();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ── Édition ────────────────────────────────────────────────
  const openEdit = (doc) => {
    setEditDoc(doc);
    setEditForm({
      type_id:  doc.upload_type_id || '',
      type_nom: doc.upload_type_nom || '',
      numero:   doc.numero || '',
      date:     doc.date_document ? String(doc.date_document).slice(0, 10) : todayISO(),
    });
  };

  const handleEditSave = async () => {
    if (!editDoc || !updateFn) return;
    setSaving(true);
    try {
      const type = types.find(t => t.type_id === editForm.type_id);
      await updateFn(editDoc.doc_id, {
        upload_type_id:  type?.type_id  || null,
        upload_type_nom: type?.nom      || null,
        numero:          editForm.numero || null,
        date_document:   editForm.date   || null,
      });
      toast.success('Document mis à jour');
      setEditDoc(null);
      await load();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-2">

      {/* Zone Drag & Drop */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="doc-dropzone"
        className="flex flex-col items-center justify-center gap-2 rounded-xl cursor-pointer transition-all"
        style={{
          border: `2px dashed ${dragging ? 'var(--primary-solid)' : 'var(--border)'}`,
          background: dragging ? 'rgba(99,102,241,0.06)' : 'var(--bg-subtle)',
          padding: '28px 20px',
          minHeight: 110,
        }}
      >
        <Upload size={24} style={{ color: dragging ? 'var(--primary-solid)' : 'var(--text-muted)' }} />
        <p className="text-sm font-medium" style={{ color: dragging ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
          Glissez un fichier ici ou{' '}
          <span style={{ color: 'var(--primary-solid)', textDecoration: 'underline' }}>parcourez</span>
        </p>
        <p className="text-xs" style={{ color: 'var(--text-disabled, #CBD5E1)' }}>
          PDF, Word, Excel, images (JPG, PNG, HEIC)…
        </p>
        <input ref={inputRef} type="file" hidden onChange={handleFileInput} data-testid="doc-file-input" />
      </div>

      {/* Liste documents */}
      {loading && (
        <p className="text-xs text-center py-3" style={{ color: 'var(--text-muted)' }}>Chargement…</p>
      )}
      {!loading && docs.length === 0 && (
        <p className="text-xs text-center py-3" style={{ color: 'var(--text-muted)' }}>
          Aucun document — déposez vos fichiers ci-dessus
        </p>
      )}

      {docs.map(doc => (
        <div
          key={doc.doc_id}
          data-testid={`doc-row-${doc.doc_id}`}
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs"
          style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', minHeight: 36 }}
        >
          {/* Type */}
          <span
            className="flex-shrink-0 px-1.5 py-0.5 rounded-full font-semibold truncate"
            style={{
              background: 'rgba(99,102,241,0.1)',
              color: 'var(--primary-solid)',
              width: wideColumns ? 220 : 200,
              minWidth: wideColumns ? 220 : 200,
            }}
            title={doc.upload_type_nom || '—'}
          >
            {doc.upload_type_nom || '—'}
          </span>

          {/* Numéro */}
          <span
            className="flex-shrink-0 font-mono truncate"
            style={{ color: 'var(--text-secondary)', width: wideColumns ? 180 : 120, minWidth: wideColumns ? 180 : 120 }}
            title={doc.numero || ''}
          >
            {doc.numero || '—'}
          </span>

          {/* Date */}
          <span className="flex-shrink-0" style={{ color: 'var(--text-muted)', width: 80, minWidth: 80 }}>
            {fmtDate(doc.date_document)}
          </span>

          {/* Nom fichier */}
          <span
            className="flex-1 truncate font-medium"
            style={{ color: 'var(--text)' }}
            title={doc.nom_fichier}
          >
            {doc.nom_fichier}
          </span>

          {/* Taille */}
          <span className="flex-shrink-0" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap', width: 46, textAlign: 'right' }}>
            {fmtSize(doc.taille)}
          </span>

          {/* Aperçu */}
          <button
            onClick={() => handlePreview(doc)}
            title="Aperçu"
            className="flex-shrink-0 p-1 rounded hover:bg-gray-100 transition-colors"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            <Eye size={13} />
          </button>

          {/* Modifier */}
          {updateFn && (
            <button
              onClick={() => openEdit(doc)}
              data-testid={`edit-doc-${doc.doc_id}`}
              title="Modifier"
              className="flex-shrink-0 p-1 rounded hover:bg-gray-100 transition-colors"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
            >
              <Pencil size={13} />
            </button>
          )}

          {/* Supprimer */}
          <button
            onClick={() => handleDelete(doc)}
            data-testid={`delete-doc-${doc.doc_id}`}
            title="Supprimer"
            className="flex-shrink-0 p-1 rounded hover:bg-red-50 transition-colors"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}
          >
            <Trash2 size={13} />
          </button>
        </div>
      ))}

      {/* Modal upload */}
      {uploadModal && (
        <DocModal
          title="Ajouter un document"
          fileName={pendingFile?.name}
          form={uploadForm}
          setForm={setUploadForm}
          types={types}
          onConfirm={handleUploadConfirm}
          onCancel={() => { setUploadModal(false); setPendingFile(null); }}
          loading={uploading}
          confirmLabel="Confirmer"
        />
      )}

      {/* Modal édition */}
      {editDoc && (
        <DocModal
          title="Modifier le document"
          fileName={editDoc.nom_fichier}
          form={editForm}
          setForm={setEditForm}
          types={types}
          onConfirm={handleEditSave}
          onCancel={() => setEditDoc(null)}
          loading={saving}
          confirmLabel="Enregistrer"
        />
      )}

      {/* Modal aperçu image */}
      {previewImg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={() => setPreviewImg(null)}
        >
          <div
            className="relative rounded-xl overflow-hidden flex flex-col"
            style={{ maxWidth: '90vw', maxHeight: '90vh', background: '#fff' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2"
              style={{ borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <span className="text-sm font-semibold truncate max-w-lg" style={{ color: '#111' }}>{previewName}</span>
              <button onClick={() => setPreviewImg(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={18} style={{ color: '#6b7280' }} />
              </button>
            </div>
            <div className="flex items-center justify-center p-4" style={{ background: '#f1f5f9' }}>
              <img src={previewImg} alt={previewName} style={{ maxWidth: '80vw', maxHeight: '80vh', objectFit: 'contain' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
